// CORE
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { makeStyles } from 'tss-react/mui';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';

import { rootUiActions } from 'companion-app-components/flux/root-ui';

// Custom
// eslint-disable-next-line import/no-named-as-default-member
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import QCheckbox from 'components/QCheckbox';

import { bulkEditConfirmTitle, resolveBulkEditConfirm } from './utils';

const useStyles = makeStyles()((theme) => ({
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: 24,
    color: theme.palette.text.strong,
  },
  icon: {
    color: theme.palette.primary.main,
    marginRight: 12,
    flexShrink: 0,
  },
  note: {
    marginTop: 12,
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
    paddingTop: 0,
  },
}));

type BulkEditConfirmDialogProps = {
  dialogId: string;
  count: number;
  hasSplitTxns: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
  onDontShowAgain: () => void;
};

const BulkEditConfirmDialog: React.FC<BulkEditConfirmDialogProps> = ({
  dialogId,
  count,
  hasSplitTxns,
  onConfirm,
  onCancel,
  onDontShowAgain,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const resolve = (confirmed: boolean) => {
    dispatch(rootUiActions.removeDialog(dialogId));
    resolveBulkEditConfirm({ confirmed, dontShowAgain, onConfirm, onCancel, onDontShowAgain });
  };

  // the close button and Escape both land here, so a dismissed dialog never saves
  const handleCancel = () => resolve(false);

  return (
    <StdDialog
      open
      showCloseButton
      onClose={handleCancel}
      onKeyDown={(event) => { if (event.key === 'Enter') { resolve(true); } }}
      title={bulkEditConfirmTitle(count)}
      sharedcomponentid="BULK_EDIT_CONFIRM_DIALOG"
      maxWidth="md"
    >
      <DialogContent className={classes.content}>
        <HelpOutlineRounded className={classes.icon} />
        <div>
          <Typography variant="body1">
            Your changes will be applied to the selected transactions. Do you want to proceed?
          </Typography>
          {hasSplitTxns && (
            <Typography variant="body2" color="text.secondary" className={classes.note}>
              Note: Some changes may not apply to split transactions.
            </Typography>
          )}
        </div>
      </DialogContent>

      <div className={classes.bottomRow}>
        <FormControlLabel
          control={<QCheckbox id="bulk-edit-dont-show-again" />}
          label="Don't show this message again"
          checked={dontShowAgain}
          onChange={(_event, checked) => setDontShowAgain(checked)}
        />

        <div>
          <QButton id="bulk-edit-cancel" variant="text" color="primary" onClick={handleCancel}>
            Cancel
          </QButton>
          <QButton id="bulk-edit-save" variant="text" color="primary" style={{ marginLeft: 8 }} onClick={() => resolve(true)}>
            Save
          </QButton>
        </div>
      </div>
    </StdDialog>
  );
};

export default BulkEditConfirmDialog;
